import clsx from 'clsx';
import _ from 'lodash-es';
import { Ref } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { Advertisement } from '@/queries/advertisements/types';
import { useSelector } from '@/redux/helper';

export interface AdvertisementOverlayProps {
  muted?: boolean;
  isVisible: boolean;
  adsPlayerRef: Ref<HTMLVideoElement>;
  advertisement?: Advertisement;
}

const AdvertisementOverlay = ({ isVisible, muted: mutedProp, adsPlayerRef, advertisement }: AdvertisementOverlayProps) => {
  const defaultMuted = useSelector((state) => state.settings.videoPlayerConfig.muted);
  const muted = mutedProp ?? defaultMuted;
  const volume = useSelector((state) => state.settings.videoPlayerConfig.volume);
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState(0.01);

  useEffect(() => {
    const adsPlayer = videoRef.current;
    const handler = _.debounce(
      () => {
        setCurrentTime(adsPlayer?.currentTime ?? 0);
        setDuration(adsPlayer?.duration ?? 0.01);
      },
      200,
      { trailing: true },
    );
    adsPlayer?.addEventListener('timeupdate', handler);
    return () => {
      adsPlayer?.removeEventListener('timeupdate', handler);
    };
  }, []);

  useEffect(() => {
    const adsPlayer = videoRef.current;
    const handler = _.debounce(
      () => {
        setDuration(adsPlayer?.duration ?? 0.01);
      },
      200,
      { trailing: true },
    );
    adsPlayer?.addEventListener('loadedmetadata', handler);
    return () => {
      adsPlayer?.removeEventListener('loadedmetadata', handler);
    };
  }, []);

  const hasAd = !!advertisement;

  // Reset the current time and duration when the ad is not visible
  useEffect(() => {
    if (!hasAd) {
      setCurrentTime(0);
      setDuration(0.01);
    }
  }, [hasAd]);

  // We shorten the duration by 0.75 seconds to ensure that the progress bar did shows 100% before the ad is removed visually
  const progress = Math.min(100, (currentTime * 100) / Math.max(0.01, duration - 0.75));

  return (
    <div
      className={clsx(
        'absolute inset-0 z-[3] flex items-center justify-center bg-black',
        (!isVisible || !hasAd) && 'hidden',
      )}
    >
      <video
        ref={(el) => {
          videoRef.current = el;
          if (adsPlayerRef && 'current' in adsPlayerRef) {
            // eslint-disable-next-line no-param-reassign
            adsPlayerRef.current = el;
          } else {
            adsPlayerRef?.(el);
          }
        }}
        muted={muted}
        volume={volume}
        autoPlay
        playsInline
        disablePictureInPicture
        src={advertisement?.mediaURL}
        className="preflight preflight-video block h-full w-full object-contain"
      />
      <div className="preflight absolute inset-x-0 bottom-0 border-b border-b-primary-green bg-gradient-to-b from-black/0 to-black/75">
        {!!advertisement?.zoo?.name && (
          <p className="preflight preflight-p p-1 pt-4 text-xs text-white desktop:p-2 desktop:pt-6 desktop:text-sm">
            Brought to you by {advertisement.zoo.name}
          </p>
        )}
        <div
          className="flex h-1 w-full overflow-hidden bg-black/75"
          role="progressbar"
          aria-valuemin={0}
          aria-valuenow={currentTime}
          aria-valuemax={duration}
        >
          <div
            className="overflow-hidden bg-white transition-all duration-500 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdvertisementOverlay;
